import { SegmentFilter } from '../segmentFilters';
import { StatItemProps } from './SegmentItem';
import { SegmentedBar, StatRow, WIDGET_COLORS, WidgetCard, WidgetTooltip, vcmIcon } from './WidgetPrimitives';

// Every VCM segment maps onto a vcm_status_for_required_users the passport report can filter
// the Educators table by, including "not started" - meaning the educator has no VCM for the
// period, which is what this widget counts an absent status as. That ransacker matches only
// the educators that require VCMs, which are the ones counted here.
// Shared by the bar segment and the stat below it so both toggle the same filter.
const vcmFilter = (status: string, label: string): SegmentFilter => ({
  attribute: 'vcm_status_for_required_users',
  value: status,
  group: 'VCM',
  label
});

const vcmStat = (percent: number, status: string, label: string, color?: string): StatItemProps => ({
  value: `${percent}%`,
  label,
  color,
  filter: vcmFilter(status, label)
});

export interface VcmWidgetData {
  total_count?: number;
  counts?: {
    approved?: number;
    draft?: number;
    in_review?: number;
    needs_attention?: number;
    not_started?: number;
  };
  percentages?: {
    approved?: number;
    draft?: number;
    in_review?: number;
    needs_attention?: number;
    not_started?: number;
  };
}

interface VcmWidgetProps {
  data: VcmWidgetData;
}

const TOOLTIP = (
  <WidgetTooltip
    heading="VCM"
    description="Shows Educator VCM status."
    definitions={[
      { term: 'Approved', description: 'Educators whose VCMs have been approved.', color: WIDGET_COLORS.green },
      {
        term: 'In Review',
        description: 'Educators whose VCMs have been submitted to a supervisor for review.',
        color: WIDGET_COLORS.blue
      },
      {
        term: 'Needs Attention',
        description: 'Educators whose VCMs have been returned for updates or amendments.',
        color: WIDGET_COLORS.yellow
      },
      { term: 'Draft', description: 'Educators who have commenced a VCM.', color: WIDGET_COLORS.black },
      {
        term: 'Not Started',
        description: 'Educators who have not started a VCM revision during the current calendar year.'
      }
    ]}
  />
);

const VcmWidget = ({
  data: {
    total_count: total = 0,
    counts: {
      approved: approvedCount = 0,
      draft: draftCount = 0,
      in_review: inReviewCount = 0,
      needs_attention: needsAttentionCount = 0,
      not_started: notStartedCount = 0
    } = {},
    percentages: {
      approved: approvedPercent = 0,
      draft: draftPercent = 0,
      in_review: inReviewPercent = 0,
      needs_attention: needsAttentionPercent = 0,
      not_started: notStartedPercent = 0
    } = {}
  }
}: VcmWidgetProps) => (
  <WidgetCard title="VCM" total={total} icon={vcmIcon} tooltip={TOOLTIP}>
    <SegmentedBar
      segments={[
        {
          percent: approvedPercent,
          color: WIDGET_COLORS.green,
          label: 'Approved',
          count: approvedCount,
          filter: vcmFilter('approved', 'Approved')
        },
        {
          percent: inReviewPercent,
          color: WIDGET_COLORS.blue,
          label: 'In review',
          count: inReviewCount,
          filter: vcmFilter('in_review', 'In review')
        },
        {
          percent: needsAttentionPercent,
          color: WIDGET_COLORS.yellow,
          label: 'Needs attention',
          count: needsAttentionCount,
          filter: vcmFilter('needs_attention', 'Needs attention')
        },
        {
          percent: draftPercent,
          color: WIDGET_COLORS.dark,
          label: 'Draft',
          count: draftCount,
          filter: vcmFilter('draft', 'Draft')
        },
        {
          percent: notStartedPercent,
          color: WIDGET_COLORS.lightGray,
          label: 'Not started',
          count: notStartedCount,
          filter: vcmFilter('not_started', 'Not started')
        }
      ]}
    />
    <StatRow
      items={[
        vcmStat(approvedPercent, 'approved', 'Approved', WIDGET_COLORS.green),
        vcmStat(inReviewPercent, 'in_review', 'In review', WIDGET_COLORS.blue),
        vcmStat(needsAttentionPercent, 'needs_attention', 'Needs attention', WIDGET_COLORS.yellow),
        vcmStat(draftPercent, 'draft', 'Draft', WIDGET_COLORS.black),
        vcmStat(notStartedPercent, 'not_started', 'Not started', WIDGET_COLORS.gray)
      ]}
    />
  </WidgetCard>
);

export default VcmWidget;
